import React, { useEffect, useState, useCallback } from 'react';
import {
    Table,
    Button,
    Input,
    Select,
    message,
    Popconfirm,
    Typography,
} from 'antd';
import {
    PhoneOutlined,
    PlusOutlined,
    CloseOutlined,
    ReloadOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CommonLayout from '../../common/LayoutBlock';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useAuthOrg } from '../../context/AuthOrgContext';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_NODE_API_ENDPOINT } from '../../utils';

const { Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExcludedNumber {
    number: string;
    createdBy: number;
    createdAt: string;
    orgIds: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return iso;
    }
};

const CardHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    action?: React.ReactNode;
}> = ({ icon, title, action }) => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-[#1F344C] text-base">
                {icon}
            </div>
            <span className="text-sm font-semibold text-gray-800">{title}</span>
        </div>
        {action}
    </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

const ExcludeNumber: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { user, availableOrgs } = useAuthOrg();

    // ── Add form state
    const [phoneNumber, setPhoneNumber] = useState('');
    const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);

    // ── List state
    const [records, setRecords] = useState<ExcludedNumber[]>([]);
    const [loading, setLoading] = useState(false);

    // ── Deleting set (tracks 'number:orgId' pairs being deleted)
    const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());

    // ─── Breadcrumbs ──────────────────────────────────────────────────────────
    useEffect(() => {
        setBreadcrumbs([
            {
                title: (
                    <div
                        className="text-lg cursor-pointer"
                        onClick={() => navigate('/settings')}
                    >
                        Settings
                    </div>
                ),
            },
            { title: <div className="text-lg">Exclude Number</div> },
        ]);
    }, [setBreadcrumbs]);

    // ─── Fetch excluded numbers ───────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        if (!user?.userId) return;
        setLoading(true);
        try {
            const res = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT,
                `common/v1/exclude-number?user_id=${user.userId}`
            );
            setRecords(res?.data || []);
        } catch (err: any) {
            message.error(err?.message || 'Failed to fetch excluded numbers');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    }, [user?.userId]);

    useEffect(() => {
        fetchRecords();
    }, []);

    // ─── Org select helpers ───────────────────────────────────────────────────
    const allOrgIds = availableOrgs.map((o) => o.orgId);
    const allSelected =
        allOrgIds.length > 0 && selectedOrgIds.length === allOrgIds.length;

    const handleOrgChange = (values: string[]) => {
        // Filter out the sentinel value
        const filtered = values.filter((v) => v !== '__ALL__');
        setSelectedOrgIds(filtered);
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedOrgIds([]);
        } else {
            setSelectedOrgIds(allOrgIds);
        }
    };

    // ─── Add ─────────────────────────────────────────────────────────────────
    const handleAdd = async () => {
        const trimmed = phoneNumber.trim();
        if (!trimmed) {
            message.warning('Please enter a phone number');
            return;
        }
        if (!/^\d{10}$/.test(trimmed)) {
            message.warning('Phone number must be exactly 10 digits');
            return;
        }
        if (!user?.userId) {
            message.error('User not found. Please reload.');
            return;
        }
        if (selectedOrgIds.length === 0) {
            message.warning('Please select at least one organisation');
            return;
        }

        setSubmitting(true);
        try {
            await coreAPIService.post<any>(CTW_NODE_API_ENDPOINT, 'common/v1/exclude-number', {
                number: trimmed,
                created_by: user.userId,
                org_ids: selectedOrgIds,
            });
            message.success('Number excluded successfully');
            setPhoneNumber('');
            setSelectedOrgIds([]);
            fetchRecords();
        } catch (err: any) {
            message.error(err?.message || 'Failed to exclude number');
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Delete a single org entry ────────────────────────────────────────────
    const handleDeleteOrg = async (number: string, orgId: string) => {
        const key = `${number}:${orgId}`;
        setDeletingKeys((prev) => new Set(prev).add(key));
        try {
            await coreAPIService.delete<any>(CTW_NODE_API_ENDPOINT, 'common/v1/exclude-number', {
                number,
                orgId,
            });
            message.success(`Removed from org`);
            fetchRecords();
        } catch (err: any) {
            message.error(err?.message || 'Failed to delete');
        } finally {
            setDeletingKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    // ─── Org name resolver ────────────────────────────────────────────────────
    const orgName = (orgId: string) => {
        // console.log(orgId);

        const org = availableOrgs.find((o) => o.orgId === orgId);
        return org?.orgName || orgId;
    };

    // ─── Table columns ────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Phone Number',
            dataIndex: 'number',
            key: 'number',
            render: (num: string) => (
                <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-7 h-7 rounded-md bg-red-50 text-red-500 text-sm flex-shrink-0">
                        <PhoneOutlined />
                    </span>
                    <Text className="text-sm font-medium text-gray-800 tabular-nums">
                        {num}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Organisations',
            dataIndex: 'orgIds',
            key: 'orgIds',
            render: (orgIds: string, record: ExcludedNumber) => (
                <div className="flex flex-wrap gap-1.5">
                    {orgIds?.split(',')?.map((id) => {
                        const key = `${record.number}:${id}`;
                        const isDeleting = deletingKeys.has(key);
                        return (
                            <Popconfirm
                                key={id}
                                title="Remove from org?"
                                description={`Remove ${record.number} from "${orgName(id)}"?`}
                                onConfirm={() => handleDeleteOrg(record.number, id)}
                                okText="Remove"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-2.5 py-0.5 cursor-pointer select-none transition-colors ${isDeleting
                                        ? 'bg-gray-100 text-gray-400'
                                        : 'bg-blue-50 text-blue-700 hover:bg-red-50 hover:text-red-600'
                                        }`}
                                >
                                    {orgName(id)}
                                    {isDeleting ? (
                                        <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <CloseOutlined className="text-[10px] opacity-60 hover:opacity-100" />
                                    )}
                                </span>
                            </Popconfirm>
                        );
                    })}
                </div>
            ),
        },
        {
            title: 'Excluded At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => (
                <span className="text-xs text-gray-400">{formatDate(date)}</span>
            ),
        },
    ];

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <CommonLayout>
            <div className="p-8 min-h-screen bg-gray-50">

                {/* ── Page Header ──────────────────────────────────── */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">
                        Exclude Number
                    </h1>
                    <p className="text-sm text-gray-500 max-w-xl">
                        Excluded numbers will not be logged in bot conversation or event logs
                        across the selected organisations.
                    </p>
                </div>

                {/* ── Add Card ──────────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-5">
                    <CardHeader icon={<StopOutlined />} title="Exclude a Phone Number" />

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

                            {/* Phone number */}
                            <div className="md:col-span-3 flex flex-col">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                                    Phone Number <span className="text-red-400 normal-case">*</span>
                                </label>
                                <Input
                                    prefix={<PhoneOutlined className="text-gray-400" />}
                                    placeholder="e.g. 9876543210"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    onPressEnter={handleAdd}
                                    maxLength={10}
                                    size="middle"
                                    className="!rounded-lg"
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                    10-digit mobile number without country code
                                </p>
                            </div>

                            {/* Org multi-select */}
                            <div className="md:col-span-7 flex flex-col">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                                    Organisations <span className="text-red-400 normal-case">*</span>
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Select organisations…"
                                    value={selectedOrgIds}
                                    onChange={handleOrgChange}
                                    className="w-full"   // ✅ FIXED
                                    maxTagCount="responsive"
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    dropdownRender={(menu) => (
                                        <div>
                                            <div
                                                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                                                onClick={handleSelectAll}
                                            >
                                                <span
                                                    className={`inline-flex items-center justify-center w-4 h-4 rounded border transition-colors ${allSelected
                                                        ? 'bg-blue-600 border-blue-600'
                                                        : 'border-gray-300'
                                                        }`}
                                                >
                                                    {allSelected && (
                                                        <svg
                                                            className="w-3 h-3 text-white"
                                                            viewBox="0 0 12 12"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M2 6l3 3 5-5"
                                                                stroke="currentColor"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {allSelected ? 'Deselect All' : 'Select All'} ({availableOrgs.length})
                                                </span>
                                            </div>
                                            {menu}
                                        </div>
                                    )}
                                    options={availableOrgs.map((org) => ({
                                        value: org.orgId,
                                        label: org.orgName,
                                    }))}
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                    Number will be excluded as lead for the selected orgs
                                </p>
                            </div>

                            {/* Submit */}
                            <div className="md:col-span-2 flex flex-col justify-end">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAdd}
                                    loading={submitting}
                                    className="w-full !bg-[#1F344C] !border-[#1F344C] !rounded-lg !font-semibold hover:!opacity-90 mb-12"
                                >
                                    Exclude
                                </Button>
                            </div>

                        </div>
                    </div>
                </div>

                {/* ── List Card ──────────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <CardHeader
                        icon={<PhoneOutlined />}
                        title="Excluded Numbers"
                        action={
                            <Button
                                icon={<ReloadOutlined />}
                                size="small"
                                onClick={fetchRecords}
                                loading={loading}
                                className="!rounded-md !text-xs !text-gray-500 !border-gray-200 hover:!text-[#1F344C] hover:!border-[#1F344C]"
                            >
                                Refresh
                            </Button>
                        }
                    />

                    <Table
                        columns={columns}
                        dataSource={records}
                        rowKey={(r) => r.number}
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            size: 'small',
                            showTotal: (t) => `${t} numbers`,
                        }}
                        scroll={{ x: true }}
                        locale={{
                            emptyText: (
                                <div className="py-12 text-center">
                                    <StopOutlined className="text-3xl text-gray-200 mb-3 block" />
                                    <p className="text-gray-400 text-sm">
                                        No excluded numbers yet.
                                    </p>
                                </div>
                            ),
                        }}
                    />
                </div>

            </div>
        </CommonLayout>
    );
};

export default ExcludeNumber;
